import {
  IModifier,
  IModifierData,
  ModifierType,
} from 'features/menu/components/product-detail/modifier/types';
import {
  getSelectedMultiplier,
  isMultiplierVisuallySelected,
  mapModifier,
} from 'features/menu/components/product-detail/modifier/utils';
import { IModifierGroup } from 'features/menu/components/product-detail/product-customization/product-customization-modifier-group';
import {
  ComboSlotType,
  DrinkCategory,
} from 'features/menu/components/product-detail/product-customization/types';
import {
  DEFAULT_SLOT_KEY,
  ModifiersSelections,
} from 'features/menu/components/product-wizard/types';

import { ItemAvailabilityStatus } from 'enums/menu';
import { EntryTypeEnum, ProductTypeDto } from 'generated/graphql-gateway';
import { IDayPartBoundary } from 'state/menu/main-menu/types';
import { ILocalizedMenuEntry, IndexedMenu, PosServiceMode } from 'state/unified-menu/types';
import {
  getComboSlotAvailableEntries,
  getFirstDefaultId,
  hasOptionEntries,
  isComboSlot,
  isEntryAvailable,
  isItem,
  mapAvailableOptions,
} from 'state/unified-menu/utils';
import { getOneMenuAvailability } from 'utils/availability/one-menu-availability';

export function groupModifiersByDisplayName(
  modifiers: IModifier[],
  modifierSelections: ModifiersSelections
): IModifierGroup[] {
  if (modifiers.length === 0) {
    return [];
  }

  const included: IModifierData[] = [];
  const addons: IModifierData[] = [];
  const singleOrMultipleChoice: IModifierData[] = [];

  for (const modifier of modifiers) {
    const modifierData: IModifierData = {
      modifier,
      selections: modifierSelections[modifier.id] || {},
    };

    // Pick selected multiplier or default multiplier if no selection
    const multiplier = getSelectedMultiplier(modifier, modifierData.selections);

    if ([ModifierType.MULTIPLE_CHOICE, ModifierType.SINGLE_CHOICE].includes(modifier.type)) {
      singleOrMultipleChoice.push(modifierData);
    } else if (isMultiplierVisuallySelected(multiplier)) {
      included.push(modifierData);
    } else {
      addons.push(modifierData);
    }
  }

  return [
    { key: 'included', displayGroupName: 'included', modifiers: included },
    { key: 'addons', displayGroupName: 'addons', modifiers: addons },
    { key: 'empty', displayGroupName: undefined, modifiers: singleOrMultipleChoice },
  ];
}

interface IGetComboSlotItems {
  menu: IndexedMenu<ILocalizedMenuEntry>;
  slotKey: string;
  posServiceMode: PosServiceMode;
  activeDayParts: IDayPartBoundary[];
  storeHasBurgersForBreakfast: boolean;
}

/**
 * Returns the items associated to a combo slot.
 * @param menu - indexed menu to look up related entries
 * @param slotKey - combo slot key to find related items.
 * @returns the items associated to the combo slot.
 */
export function getComboSlotItems({
  menu,
  slotKey,
  posServiceMode,
  activeDayParts = [],
  storeHasBurgersForBreakfast = false,
}: IGetComboSlotItems): ILocalizedMenuEntry[] {
  const comboSlot = menu.get(slotKey, { type: EntryTypeEnum.COMBO_SLOT });

  const items = mapAvailableOptions(
    menu,
    posServiceMode,
    comboSlot.options?.entries,
    ({ entryId, price: upsell }) => {
      const item = menu.get(entryId, { type: EntryTypeEnum.ITEM });
      return { ...item, price: upsell ?? item.price };
    }
  );

  const filteredItemsByAvailability = items.filter(item => {
    const availability = getOneMenuAvailability(
      item,
      activeDayParts,
      posServiceMode,
      storeHasBurgersForBreakfast,
      isEntryAvailable
    );
    const isAvailable = availability === ItemAvailabilityStatus.AVAILABLE;
    return isAvailable;
  });

  return filteredItemsByAvailability;
}

/**
 * Returns the type of a combo slot.
 * @param menu - indexed menu to look up related entries
 * @param slotKey - combo slot key.
 * @returns the type of the combo slot.
 */
export function getComboSlotType(
  menu: IndexedMenu<ILocalizedMenuEntry>,
  slotKey: string
): ComboSlotType {
  if (slotKey && slotKey !== DEFAULT_SLOT_KEY) {
    const comboSlot = menu.get(slotKey, { type: EntryTypeEnum.COMBO_SLOT });

    const hasSides = comboSlot.productType?.includes(ProductTypeDto.SIDES) ?? false;
    const hasDrinks = comboSlot.productType?.includes(ProductTypeDto.DRINKS) ?? false;

    // Some products combine both sides and drinks
    if (hasDrinks && !hasSides) {
      return ComboSlotType.DRINKS;
    }
    if (hasSides && !hasDrinks) {
      return ComboSlotType.SIDES;
    }
  }
  return ComboSlotType.PICKER_ASPECT;
}

export const getDrinkCategory = (item: ILocalizedMenuEntry) => {
  if (!item.productType) {
    return DrinkCategory.OTHERS;
  } else if (item.productType.includes(ProductTypeDto.SOFT_DRINKS)) {
    return DrinkCategory.SOFTS;
  } else if (item.productType.includes(ProductTypeDto.SHAKES)) {
    return DrinkCategory.SHAKES;
  } else if (item.productType.includes(ProductTypeDto.FROZEN_DRINKS)) {
    return DrinkCategory.FROZENS;
  }
  return DrinkCategory.OTHERS;
};

/**
 * Whether the entry is customizable.
 * In case of "item" because it has modifiers that requires user interaction.
 * In case of "combo" because it has slots.
 * In case of "comboSlot" because it has multiple slot options.
 * In case of "picker" because it has picker aspects.
 * @param entry - entry to check
 * @returns true if the entry is customizable
 */
export function isCustomizableEntry(
  entry: ILocalizedMenuEntry,
  menu: IndexedMenu<ILocalizedMenuEntry>,
  posServiceMode: PosServiceMode,
  activeDayParts?: IDayPartBoundary[],
  storeHasBurgersForBreakfast?: boolean
): boolean {
  if (isComboSlot(entry)) {
    const comboSlotAvailableEntries = getComboSlotAvailableEntries({
      menu,
      posServiceMode,
      entries: entry.options?.entries,
      activeDayParts,
      storeHasBurgersForBreakfast,
    });

    if (comboSlotAvailableEntries.length > 1) {
      return true;
    }

    if (comboSlotAvailableEntries.length === 1) {
      const item = menu.get(comboSlotAvailableEntries[0].id);

      if (!hasOptionEntries(posServiceMode, item)) {
        return false;
      }
    }

    const item = menu.get(getFirstDefaultId(entry, true));

    return isCustomizableEntry(
      item,
      menu,
      posServiceMode,
      activeDayParts,
      storeHasBurgersForBreakfast
    );
  }

  const hasOptions = hasOptionEntries(posServiceMode, entry);
  if (!isItem(entry)) {
    // handles combo, and picker
    return hasOptions;
  }

  if (!hasOptions) {
    // item does not have modifiers
    return false;
  }

  const modifiersCount = entry.options.entries.length;
  return entry.options.entries.some(({ entryId, quantity }) => {
    const mappedModifier = mapModifier(menu, entryId, modifiersCount, posServiceMode, quantity);

    if (!mappedModifier || !mappedModifier.multipliers?.length) {
      return false;
    }

    const isSingleOrMultipleChoiceModifier =
      mappedModifier.type === ModifierType.MULTIPLE_CHOICE ||
      mappedModifier.type === ModifierType.SINGLE_CHOICE;
    return mappedModifier.multipliers.length > (isSingleOrMultipleChoiceModifier ? 0 : 1);
  });
}
